import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { clienteApi, setClienteToken } from "@/lib/api";
import { useClienteAuth } from "@/lib/clienteAuth";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck, ArrowLeft } from "lucide-react";

export default function CadastroCliente() {
  const { codigo } = useParams();
  const nav = useNavigate();
  const { refresh } = useClienteAuth();

  const [info, setInfo] = useState(null); // {nome, ja_cadastrado, email_cadastrado}
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    clienteApi.get(`/cliente-auth/cadastro/${codigo}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setErro(e?.response?.data?.detail || "Link inválido ou expirado."))
      .finally(() => setCarregando(false));
  }, [codigo]);

  const cadastrar = async (e) => {
    e.preventDefault();
    if (!email || !senha) return;
    if (senha.length < 6) return toast.error("A senha precisa ter pelo menos 6 caracteres.");
    setEnviando(true);
    try {
      const { data } = await clienteApi.post("/cliente-auth/registrar", {
        codigo_cadastro: codigo,
        email: email.trim().toLowerCase(),
        senha,
      });
      // Fallback: guarda o token caso o cookie seja bloqueado
      if (data?.session_token) setClienteToken(data.session_token);
      await refresh();
      toast.success("Conta criada! Bem-vindo(a).");
      nav("/portal", { replace: true });
    } catch (e2) {
      const msg = formatErr(e2?.response?.data?.detail);
      toast.error(msg);
    } finally {
      setEnviando(false);
    }
  };

  if (carregando) {
    return <FullScreen>Carregando…</FullScreen>;
  }
  if (erro) {
    return (
      <FullScreen>
        <div className="text-center max-w-md">
          <div className="text-rose-600 font-semibold mb-2">Link inválido</div>
          <div className="text-sm text-zinc-600 mb-6">{erro}</div>
          <Link to="/" className="text-sm underline text-zinc-700">Voltar à página inicial</Link>
        </div>
      </FullScreen>
    );
  }

  if (info?.ja_cadastrado) {
    return (
      <FullScreen>
        <Card>
          <div className="text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-3">
              <ShieldCheck className="w-6 h-6 text-amber-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-1">Conta já cadastrada</h1>
            <p className="text-sm text-zinc-600 mb-6">
              Já existe uma conta vinculada a <b>{info.nome}</b>{info.email_cadastrado ? ` (${info.email_cadastrado})` : ""}. Faça login para acessar seus orçamentos.
            </p>
            <Link
              to="/portal/login"
              data-testid="ir-login-btn"
              className="inline-block w-full text-center px-4 py-2.5 rounded-md bbh-btn-primary text-sm font-semibold"
            >
              Ir para o login
            </Link>
          </div>
        </Card>
      </FullScreen>
    );
  }

  return (
    <FullScreen>
      <Card>
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 mb-4">
          <ArrowLeft className="w-3 h-3" /> Voltar
        </Link>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">
          Bem-vindo(a)
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-1">
          Criar conta — {info?.nome}
        </h1>
        <p className="text-sm text-zinc-600 mb-6">
          Cadastre-se para acessar todos os seus orçamentos com a BBH em um só lugar.
        </p>
        <form onSubmit={cadastrar} className="space-y-4" data-testid="cadastro-form">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">E-mail</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="cadastro-email"
              autoComplete="email"
              className="mt-1 w-full px-3 py-2.5 rounded-md border border-zinc-200 bbh-input text-sm"
              placeholder="seu@email.com"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Senha</label>
            <div className="relative mt-1">
              <input
                type={mostrarSenha ? "text" : "password"}
                required
                minLength={6}
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                data-testid="cadastro-senha"
                autoComplete="new-password"
                className="w-full px-3 py-2.5 pr-10 rounded-md border border-zinc-200 bbh-input text-sm"
                placeholder="Mínimo 6 caracteres"
              />
              <button
                type="button"
                onClick={() => setMostrarSenha((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-400 hover:text-zinc-700"
                aria-label="Mostrar/ocultar senha"
              >
                {mostrarSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={enviando}
            data-testid="cadastro-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bbh-btn-primary text-sm font-semibold disabled:opacity-50"
          >
            {enviando ? "Criando conta…" : "Criar conta e acessar"}
          </button>
        </form>
        <div className="text-center text-xs text-zinc-500 mt-4">
          Já tem conta? <Link to="/portal/login" className="underline text-zinc-800">Entrar</Link>
        </div>
      </Card>
    </FullScreen>
  );
}

function FullScreen({ children }) {
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      {children}
    </div>
  );
}

function Card({ children }) {
  return <div className="w-full max-w-md bbh-card p-6 sm:p-8">{children}</div>;
}

function formatErr(detail) {
  if (!detail) return "Não foi possível concluir. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}
